/**
 * durationUtil.js
 * Utility bersama untuk menampilkan durasi voucher secara akurat di semua
 * template cetak. Menggunakan `package_name` sebagai source of truth
 * karena DB field `validity_days` / `validity_hours` sering tidak konsisten:
 *  - Paket "HOTSPOT 12 JAM MAYAK" sering diisi validity_days=12
 *  - Paket "1 JAM" diisi validity_days=1
 *  → logika lama `validity_days===1 ? 'Hari' : 'Jam'` salah untuk paket
 *    "1 JAM" (menampilkan "1 Hari"), padahal package name-nya jelas JAM.
 *
 * Cara pakai:
 *   import { formatVoucherDuration } from './durationUtil';
 *   ...
 *   {formatVoucherDuration(voucher)}   // → "12 Jam" / "1 Jam" / "3 Hari" ...
 */

export function formatVoucherDuration(voucher) {
  if (!voucher) return '';
  const name =
    voucher.package_name ||
    voucher.service_plan ||
    voucher.plan_name ||
    '';

  // Deteksi unlimited via nama paket (mis. "Free ...", "Unlimited")
  const nameLower = String(name).toLowerCase();
  const nameIsUnlimited =
    /\bunlimited\b/.test(nameLower) ||
    /\btanpa batas\b/.test(nameLower) ||
    /\bunli\b/.test(nameLower) ||
    /^\s*free\b/.test(nameLower);

  // Regex tangkap "N UNIT" dari nama paket (case-insensitive).
  // Support: JAM, HARI, MENIT, HR, HOUR(S), DAY(S), MIN(UTE)(S)
  const m = String(name).match(
    /(\d+(?:[.,]\d+)?)\s*(JAM|HARI|HR|HOUR|HOURS|DAY|DAYS|MENIT|MIN|MINUTE|MINUTES)\b/i
  );

  if (m) {
    const n = m[1].replace(',', '.');
    const u = m[2].toUpperCase();
    let unit = 'Jam';
    if (u === 'HARI' || u === 'DAY' || u === 'DAYS') unit = 'Hari';
    else if (u === 'MENIT' || u.startsWith('MIN')) unit = 'Menit';
    // JAM / HR / HOUR / HOURS → default 'Jam'
    return `${n} ${unit}`;
  }

  // Fallback ke field DB
  const vh = Number(voucher.validity_hours || 0);
  if (vh > 0) return `${vh} Jam`;
  const vd = Number(voucher.validity_days || 0);
  if (vd > 0) {
    // Interpretasi unit dari validity_unit bila tersedia
    const u = String(voucher.validity_unit || '').toLowerCase();
    if (u === 'hours' || u === 'hour' || u === 'jam') return `${vd} Jam`;
    if (u === 'minutes' || u === 'minute' || u === 'menit' || u === 'min') return `${vd} Menit`;
    if (u === 'weeks' || u === 'week' || u === 'minggu') return `${vd} Minggu`;
    if (u === 'months' || u === 'month' || u === 'bulan') return `${vd} Bulan`;
    return `${vd} Hari`;
  }

  // 2026-07-19: Semua duration field 0/null → paket unlimited
  // (Konvensi UI: "0 = Berlaku Unlimited"). Juga trigger bila nama paket
  // eksplisit mengandung "unlimited" / "free" / "tanpa batas".
  if (nameIsUnlimited || vh === 0 || vd === 0) return 'Unlimited';
  return '';
}
